<template>
    <Head :title="`Edit: ${policy.name}`" />

    <div class="max-w-3xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow rounded-lg">
            <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <div class="flex items-center justify-between">
                    <div class="flex items-center gap-3">
                        <div class="flex-1">
                            <h2 class="text-xl font-semibold text-gray-900 dark:text-white">Edit Notification Policy</h2>
                            <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">Update notification policy settings and recipients</p>
                        </div>
                        <InfoButton title="Editing Notification Policies" variant="default">
                            <p>
                                <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                                The Edit Notification Policy page allows you to modify an existing automated email notification
                                rule that triggers when incidents matching specific criteria are reported. You can update matching
                                conditions (incident type, near miss filter), revise recipient lists (individual users or
                                role-based groups), change policy names and descriptions, and toggle active status. Changes to
                                notification policies apply immediately - newly reported incidents will trigger notifications based
                                on current active policy configurations. Existing incidents that were already reported are not
                                affected by policy changes.
                            </p>
                            <p>
                                <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                                • <strong>Policy Name:</strong> Update the descriptive name to clearly identify the notification
                                  purpose and routing logic<br>
                                • <strong>Description:</strong> Revise the explanation of what this policy covers, who receives
                                  notifications, and why this routing was established<br>
                                • <strong>Incident Type:</strong> Change which incident type triggers this policy, or select "All
                                  Types" to notify for any incident type<br>
                                • <strong>Near Miss Only:</strong> Toggle whether this policy only applies to near miss incidents
                                  or applies to both incidents and near misses<br>
                                • <strong>Recipient Type:</strong> Switch between "Specific Users" (notify individual people) and
                                  "Roles" (notify all users assigned to specific roles)<br>
                                • <strong>Select Users/Roles:</strong> Update the checkboxes to change who receives notifications
                                  - you must have at least one recipient selected<br>
                                • <strong>Selection Counter:</strong> Shows current count of selected users or roles<br>
                                • <strong>Active Status:</strong> Check to activate or uncheck to deactivate - inactive policies
                                  don't send notifications but are preserved for historical reference<br>
                                • Click <strong>Update Policy</strong> to save changes, or <strong>Cancel</strong> to discard<br>
                                • Note: Changing from users to roles (or vice versa) will clear your recipient selection - review
                                  carefully before saving
                            </p>
                            <p>
                                <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                                • <strong>Changes apply immediately</strong> - Newly reported incidents will use updated policy
                                  configurations once you save<br>
                                • <strong>Switching recipient types clears selections</strong> - If you change from "Specific
                                  Users" to "Roles" (or vice versa), you'll need to reselect recipients<br>
                                • <strong>Communicate changes to teams</strong> - If you significantly modify who receives
                                  notifications, inform affected personnel<br>
                                • <strong>Prefer role-based recipients</strong> - Roles are more maintainable than user lists
                                  because they don't break when staff changes<br>
                                • <strong>Test with inactive status</strong> - If making major changes, consider deactivating
                                  the policy, making changes, reviewing, then reactivating<br>
                                • <strong>Monitor for notification overload</strong> - If a policy is generating too many
                                  notifications, narrow the matching criteria or reduce recipients<br>
                                • <strong>Keep recipient lists relevant</strong> - Periodically review and update who's receiving
                                  notifications to ensure the lists remain appropriate<br>
                                • <strong>Deactivate instead of delete</strong> - Keep historical notification policies inactive
                                  rather than deleting to maintain audit trail<br>
                                • <strong>Document rationale for changes</strong> - Update description field to explain why
                                  routing was modified<br>
                                • <strong>Consider impact on response times</strong> - Removing key personnel from notification
                                  lists may delay incident response<br>
                                • <strong>Validate email addresses</strong> - Ensure new recipients have valid email addresses
                                  and actually check their email<br>
                                • <strong>Coordinate with SLA policies</strong> - Notification policies alert people, SLA policies
                                  enforce response timeframes - keep them aligned<br>
                                • <strong>Broadening criteria affects volume</strong> - Changing from specific incident types to
                                  "All Types" will increase notification volume<br>
                                • <strong>Near miss filter changes matter</strong> - Adding or removing near miss filter affects
                                  which incidents trigger notifications<br>
                                • Changes take effect immediately for new incidents - incidents already reported don't trigger
                                  retroactive notifications
                            </p>
                        </InfoButton>
                    </div>
                    <Link
                        :href="route('settings.incidents.notification-policies.index')"
                        class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                        Back
                    </Link>
                </div>
            </div>

            <form @submit.prevent="submit" class="px-6 py-6">
                <div class="space-y-6">
                    <!-- Name -->
                    <div>
                        <label for="name" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Policy Name <span class="text-red-500 dark:text-red-400">*</span>
                        </label>
                        <input
                            type="text"
                            id="name"
                            v-model="form.name"
                            required
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            :class="{ 'border-red-300': form.errors.name }"
                        />
                        <p v-if="form.errors.name" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.name }}</p>
                    </div>

                    <!-- Description -->
                    <div>
                        <label for="description" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
                        <textarea
                            id="description"
                            v-model="form.description"
                            rows="3"
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        ></textarea>
                    </div>

                    <!-- Matching Criteria Section -->
                    <div class="pt-6 border-t border-gray-200 dark:border-gray-700">
                        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Matching Criteria</h3>
                        <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">Leave criteria empty to match all incidents. More specific policies take precedence.</p>

                        <div class="space-y-4">
                            <!-- Incident Type -->
                            <div>
                                <label for="incident_type_id" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Incident Type</label>
                                <select
                                    id="incident_type_id"
                                    v-model="form.incident_type_id"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                >
                                    <option value="">All Types</option>
                                    <option v-for="type in incidentTypes" :key="type.id" :value="type.id">
                                        {{ type.name }}
                                    </option>
                                </select>
                                <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Leave empty to apply to all incident types</p>
                            </div>

                            <!-- Near Miss Only -->
                            <div class="flex items-start">
                                <div class="flex items-center h-5">
                                    <input
                                        id="near_miss_only"
                                        v-model="form.near_miss_only"
                                        type="checkbox"
                                        class="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded"
                                    />
                                </div>
                                <div class="ml-3 text-sm">
                                    <label for="near_miss_only" class="font-medium text-gray-700 dark:text-gray-300">Near Miss Only</label>
                                    <p class="text-gray-500 dark:text-gray-400">Only apply this policy to near miss incidents</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Recipients Section -->
                    <div class="pt-6 border-t border-gray-200 dark:border-gray-700">
                        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Notification Recipients</h3>

                        <!-- Recipient Type -->
                        <div class="mb-4">
                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Recipient Type <span class="text-red-500 dark:text-red-400">*</span>
                            </label>
                            <div class="space-y-2">
                                <label class="inline-flex items-center mr-6">
                                    <input
                                        type="radio"
                                        v-model="form.recipient_type"
                                        value="users"
                                        class="rounded-full border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span class="ml-2 text-gray-900 dark:text-white">Specific Users</span>
                                </label>
                                <label class="inline-flex items-center">
                                    <input
                                        type="radio"
                                        v-model="form.recipient_type"
                                        value="roles"
                                        class="rounded-full border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span class="ml-2 text-gray-900 dark:text-white">Roles</span>
                                </label>
                            </div>
                        </div>

                        <!-- Users Selection -->
                        <div v-if="form.recipient_type === 'users'">
                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Select Users <span class="text-red-500 dark:text-red-400">*</span>
                            </label>
                            <div class="max-h-48 overflow-y-auto border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded-md p-3">
                                <label v-for="user in users" :key="user.id" class="flex items-center py-2 hover:bg-gray-50 dark:hover:bg-gray-600 px-2 rounded cursor-pointer">
                                    <input
                                        type="checkbox"
                                        :value="user.id"
                                        v-model="form.recipient_ids"
                                        class="rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span class="ml-3 text-sm text-gray-900 dark:text-white">{{ user.name }}</span>
                                    <span class="ml-2 text-xs text-gray-500 dark:text-gray-400">({{ user.email }})</span>
                                </label>
                            </div>
                            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">{{ form.recipient_ids.length }} user(s) selected</p>
                        </div>

                        <!-- Roles Selection -->
                        <div v-if="form.recipient_type === 'roles'">
                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Select Roles <span class="text-red-500 dark:text-red-400">*</span>
                            </label>
                            <div class="max-h-48 overflow-y-auto border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded-md p-3">
                                <label v-for="role in roles" :key="role.id" class="flex items-center py-2 hover:bg-gray-50 dark:hover:bg-gray-600 px-2 rounded cursor-pointer">
                                    <input
                                        type="checkbox"
                                        :value="role.id"
                                        v-model="form.recipient_ids"
                                        class="rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span class="ml-3 text-sm text-gray-900 dark:text-white">{{ role.name }}</span>
                                </label>
                            </div>
                            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">{{ form.recipient_ids.length }} role(s) selected</p>
                        </div>
                    </div>

                    <!-- Active Status -->
                    <div class="pt-6 border-t border-gray-200 dark:border-gray-700">
                        <div class="flex items-start">
                            <div class="flex items-center h-5">
                                <input
                                    id="is_active"
                                    v-model="form.is_active"
                                    type="checkbox"
                                    class="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded"
                                />
                            </div>
                            <div class="ml-3 text-sm">
                                <label for="is_active" class="font-medium text-gray-700 dark:text-gray-300">Active</label>
                                <p class="text-gray-500 dark:text-gray-400">Enable this policy to start sending notifications</p>
                            </div>
                        </div>
                    </div>

                    <!-- Form Actions -->
                    <div class="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                        <Link
                            :href="route('settings.incidents.notification-policies.index')"
                            class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                        >
                            Cancel
                        </Link>
                        <button
                            type="submit"
                            :disabled="form.processing || form.recipient_ids.length === 0"
                            class="inline-flex justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                        >
                            {{ form.processing ? 'Updating...' : 'Update Policy' }}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script setup>
import { Head, Link } from '@inertiajs/vue3';
import { useSecureForm } from '@/Composables/useSecureForm';
import route from '@/Utils/route';
import InfoButton from '@/Components/Common/InfoButton.vue';

const props = defineProps({
    policy: {
        type: Object,
        required: true,
    },
    incidentTypes: {
        type: Array,
        required: true,
    },
    users: {
        type: Array,
        required: true,
    },
    roles: {
        type: Array,
        required: true,
    },
});

// Determine recipient type and IDs from policy
const recipientType = props.policy.notify_users?.length > 0 ? 'users' : 'roles';
const recipientIds = recipientType === 'users'
    ? props.policy.notify_users
    : props.policy.notify_roles;

// Initialize form with useSecureForm for company context protection
const form = useSecureForm({
    name: props.policy.name,
    description: props.policy.description,
    incident_type_id: props.policy.incident_type_id || '',
    near_miss_only: props.policy.near_miss_only || false,
    recipient_type: recipientType,
    recipient_ids: recipientIds,
    is_active: props.policy.is_active,
});

const submit = () => {
    form.put(route('settings.incidents.notification-policies.update', props.policy.id), {
        preserveScroll: true,
    });
};
</script>
